import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth } from '../../utils/api';

// Use relative paths — fetchAuth in api.js handles the correct base URL

function PaymentLedgerView({ user, handleApiError, showNotification }) {
    const [summary, setSummary] = useState({ totalBilled: 0, totalPaid: 0, totalOutstanding: 0, pendingInvoices: 0, overdueCount: 0 });
    const [sales, setSales] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('all');
    const [customerLedger, setCustomerLedger] = useState(null);
    const [paymentModal, setPaymentModal] = useState(null); // { saleId, customerId, remaining }
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [paymentNote, setPaymentNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await fetchAuth('/api/payments/summary');
            if (res.ok) setSummary(await res.json());
        } catch (e) { handleApiError(e); }
    }, [handleApiError]);

    const fetchSales = useCallback(async (custId) => {
        setIsLoading(true);
        try {
            const url = custId && custId !== 'all'
                ? `/api/payments?customerId=${custId}`
                : '/api/payments';
            const res = await fetchAuth(url);
            if (res.ok) setSales(await res.json());
        } catch (e) { handleApiError(e); }
        setIsLoading(false);
    }, [handleApiError]);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await fetchAuth('/api/customers');
            if (res.ok) {
                const data = await res.json();
                setCustomers(data.customers || data || []);
            }
        } catch (e) { /* silent */ }
    }, []);

    useEffect(() => {
        fetchSummary();
        fetchSales('all');
        fetchCustomers();
    }, [fetchSummary, fetchSales, fetchCustomers]);

    const handleCustomerFilter = (custId) => {
        setSelectedCustomerId(custId);
        setCustomerLedger(null);
        fetchSales(custId);
    };

    const openCustomerLedger = async (customerId) => {
        try {
            const res = await fetchAuth(`/api/payments/customer/${customerId}`);
            if (res.ok) setCustomerLedger(await res.json());
        } catch (e) { handleApiError(e); }
    };

    const recordPayment = async () => {
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            showNotification('Enter a valid payment amount.', 'error');
            return;
        }
        try {
            const res = await fetchAuth('/api/payments', {
                method: 'POST',
                body: JSON.stringify({
                    saleId: paymentModal.saleId,
                    customerId: paymentModal.customerId,
                    amountPaid: parseFloat(paymentAmount),
                    paymentMethod,
                    notes: paymentNote,
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            showNotification(`Payment recorded! Status: ${data.newStatus}. Remaining: ₹${data.remaining.toFixed(2)}`, 'success');
            setPaymentModal(null);
            setPaymentAmount('');
            setPaymentNote('');
            fetchSummary();
            fetchSales(selectedCustomerId);
            if (customerLedger) openCustomerLedger(paymentModal.customerId);
        } catch (e) { handleApiError(e); }
    };

    const isOverdue = (sale) => {
        if (sale.paymentStatus === 'Paid') return false;
        return sale.dueDate && new Date(sale.dueDate) < new Date();
    };

    const fmt = (v) => `₹${Number(v || 0).toFixed(2)}`;
    const remaining = (s) => Number(s.totalAmount || 0) - Number(s.amountPaid || 0);

    return (
        <div className="page-container">
            {/* Payment Recording Modal */}
            {paymentModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Record Payment</h3>
                        <p style={{ color: 'var(--color-text-muted)', marginBottom: '16px' }}>
                            Outstanding: <strong style={{ color: 'var(--color-danger)' }}>{fmt(paymentModal.remaining)}</strong>
                        </p>
                        <div className="input-group">
                            <label>Amount Paid (₹)</label>
                            <input
                                type="number"
                                value={paymentAmount}
                                onChange={e => setPaymentAmount(e.target.value)}
                                placeholder="Enter amount received"
                                autoFocus
                            />
                        </div>
                        <div className="input-group">
                            <label>Payment Method</label>
                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                <option>Cash</option>
                                <option>Online</option>
                                <option>Cheque</option>
                            </select>
                        </div>
                        <div className="input-group">
                            <label>Notes (Optional)</label>
                            <input value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="e.g. Partial UPI payment" />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setPaymentModal(null)}>Cancel</button>
                            <button className="confirm-btn" onClick={recordPayment}>Record Payment</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="page-header">
                <h2 className="page-title">Payment Ledger</h2>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                {[
                    { label: 'Total Billed', value: fmt(summary.totalBilled), color: 'var(--color-primary)' },
                    { label: 'Total Collected', value: fmt(summary.totalPaid), color: 'var(--color-success)' },
                    { label: 'Outstanding', value: fmt(summary.totalOutstanding), color: 'var(--color-warning)' },
                    { label: 'Overdue', value: `${summary.overdueCount || 0} invoice(s)`, color: 'var(--color-danger)' },
                ].map(card => (
                    <div key={card.label} style={{
                        background: 'var(--color-surface)', borderRadius: '12px',
                        padding: '20px', border: '1px solid var(--color-border)'
                    }}>
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>{card.label}</p>
                        <p style={{ fontSize: '22px', fontWeight: '700', color: card.color }}>{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Customer Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: '600' }}>Filter by Customer:</label>
                <select value={selectedCustomerId} onChange={e => handleCustomerFilter(e.target.value)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                    <option value="all">All Customers</option>
                    {customers.map(c => (
                        <option key={c._id || c.id} value={c._id || c.id}>{c.name} ({c.phone || 'No phone'})</option>
                    ))}
                </select>
                {selectedCustomerId !== 'all' && (
                    <button className="btn-secondary" onClick={() => openCustomerLedger(selectedCustomerId)}>
                        View Full Customer Ledger
                    </button>
                )}
            </div>

            {/* Customer Ledger Panel */}
            {customerLedger && (
                <div style={{ background: 'var(--color-surface)', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                            <h3 style={{ margin: 0 }}>{customerLedger.customer.name}</h3>
                            <p style={{ color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                                📞 {customerLedger.customer.phone || 'N/A'} &nbsp;|&nbsp; ✉️ {customerLedger.customer.email || 'N/A'}
                            </p>
                        </div>
                        <button onClick={() => setCustomerLedger(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        {[
                            { label: 'Total Billed', value: fmt(customerLedger.sales.reduce((a, s) => a + Number(s.totalAmount || 0), 0)) },
                            { label: 'Total Paid', value: fmt(customerLedger.sales.reduce((a, s) => a + Number(s.amountPaid || 0), 0)) },
                            { label: 'Outstanding', value: fmt(customerLedger.sales.reduce((a, s) => a + remaining(s), 0)) },
                        ].map(c => (
                            <div key={c.label} style={{ background: 'var(--color-bg-light)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>{c.label}</p>
                                <p style={{ fontWeight: '700', fontSize: '18px' }}>{c.value}</p>
                            </div>
                        ))}
                    </div>
                    <h4>Purchase History</h4>
                    {customerLedger.sales.length === 0 ? (
                        <p style={{ color: 'var(--color-text-muted)' }}>No sales recorded for this customer.</p>
                    ) : (
                        <table className="inventory-table">
                            <thead><tr><th>Date</th><th>Items</th><th>Total</th><th>Paid</th><th>Remaining</th><th>Status</th></tr></thead>
                            <tbody>
                                {customerLedger.sales.map(sale => (
                                    <tr key={sale._id || sale.id}>
                                        <td>{new Date(sale.saleDate || sale.sale_date).toLocaleDateString('en-IN')}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {(sale.items || []).map((it, i) => (
                                                <div key={i}>{it.nameAtSale || it.name_at_sale} × {it.quantity}</div>
                                            ))}
                                        </td>
                                        <td>{fmt(sale.totalAmount)}</td>
                                        <td style={{ color: 'var(--color-success)' }}>{fmt(sale.amountPaid)}</td>
                                        <td style={{ color: remaining(sale) > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>{fmt(remaining(sale))}</td>
                                        <td>
                                            <span style={{
                                                fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                                                background: (sale.paymentStatus === 'Paid' || sale.payment_status === 'Paid') ? 'var(--color-success-light, #d4edda)' : 'var(--color-warning-light, #fff3cd)',
                                                color: (sale.paymentStatus === 'Paid' || sale.payment_status === 'Paid') ? 'var(--color-success)' : 'var(--color-warning)',
                                            }}>
                                                {sale.paymentStatus || sale.payment_status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* All Sales List */}
            <div className="data-display">
                <h3>Invoices</h3>
                {isLoading ? <p>Loading...</p> : (
                    <div className="inventory-table-container">
                        <table className="inventory-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Customer</th>
                                    <th>Total</th>
                                    <th>Paid</th>
                                    <th>Remaining</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sales.map(sale => {
                                    const rem = remaining(sale);
                                    const paid = sale.paymentStatus === 'Paid' || sale.payment_status === 'Paid';
                                    const overdue = isOverdue(sale);
                                    return (
                                        <tr key={sale._id || sale.id} style={{ backgroundColor: overdue ? 'rgba(var(--danger-rgb, 239,68,68), 0.05)' : '' }}>
                                            <td>{new Date(sale.saleDate || sale.sale_date).toLocaleDateString('en-IN')}</td>
                                            <td>
                                                <button
                                                    onClick={() => openCustomerLedger(sale.customerId || sale.customer_id)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        color: 'var(--color-primary)', fontWeight: '600',
                                                        padding: 0, textAlign: 'left'
                                                    }}
                                                >
                                                    {sale.customerName || sale.customer_name || 'N/A'}
                                                </button>
                                            </td>
                                            <td>{fmt(sale.totalAmount)}</td>
                                            <td style={{ color: 'var(--color-success)' }}>{fmt(sale.amountPaid)}</td>
                                            <td style={{ color: rem > 0 ? 'var(--color-danger)' : 'var(--color-success)', fontWeight: rem > 0 ? '700' : '400' }}>{fmt(rem)}</td>
                                            <td>
                                                {sale.dueDate || sale.due_date
                                                    ? <span style={{ color: overdue ? 'var(--color-danger)' : 'inherit' }}>
                                                        {new Date(sale.dueDate || sale.due_date).toLocaleDateString('en-IN')}
                                                        {overdue && ' ⚠️'}
                                                    </span>
                                                    : '—'
                                                }
                                            </td>
                                            <td>
                                                <span style={{
                                                    fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                                                    background: paid ? 'var(--color-success-light, #d4edda)' : 'var(--color-warning-light, #fff3cd)',
                                                    color: paid ? 'var(--color-success)' : 'var(--color-warning)',
                                                }}>
                                                    {sale.paymentStatus || sale.payment_status}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={() => openCustomerLedger(sale.customerId || sale.customer_id)}
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            fontSize: '11px', padding: '5px 10px',
                                                            borderRadius: '6px', border: '1.5px solid var(--color-primary)',
                                                            background: 'transparent', color: 'var(--color-primary)',
                                                            cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap',
                                                            transition: 'all 0.15s ease',
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = '#fff'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                                                    >
                                                        👁 Ledger
                                                    </button>
                                                    {!paid && (
                                                        <button
                                                            onClick={() => setPaymentModal({ saleId: sale._id || sale.id, customerId: sale.customerId || sale.customer_id, remaining: rem })}
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                fontSize: '11px', padding: '5px 10px',
                                                                borderRadius: '6px', border: 'none',
                                                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                                                color: '#fff', cursor: 'pointer', fontWeight: '600',
                                                                whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(34,197,94,0.35)',
                                                                transition: 'all 0.15s ease',
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(34,197,94,0.5)'}
                                                            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(34,197,94,0.35)'}
                                                        >
                                                            💰 Collect
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {sales.length === 0 && <p style={{ textAlign: 'center', padding: '32px', color: 'var(--color-text-muted)' }}>No invoices found.</p>}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PaymentLedgerView;
